import { TermsUI } from '@axo/form/feature/terms-and-conditions/src/components/TermsAndConditions/TermsAndConditionsUI';
import { useTranslation } from '@axo/shared/i18n';
import { MarketCountryCode } from '@axo/shared/types';
import { Text } from '@axo/ui-core/components/typography';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useAxoFormContext } from '../../../context/AxoForm/AxoForm';
import { TermsAndConditionsType } from '../@types';
import type { InnerConsentProps } from '../Consent';
import styles from '../Consent.module.scss';
import { TermsModal } from './TermsModal';

export const TermsAndConditions = ({
  visible,
  external,
  terms,
  version,
}: InnerConsentProps) => {
  const { t } = useTranslation();

  const [selectedTerm, setSelectedTerm] = useState<TermsAndConditionsType>();

  const { register } = useFormContext();

  const { marketCountry } = useAxoFormContext();

  const termsComponents = useMemo(() => {
    if (!terms)
      return [
        <span key="privacy_loading" />,
        <span key="privacy_policy_loading" />,
      ];

    // TODO: Remove when the new consent config is the default
    if (!external) {
      if (marketCountry === MarketCountryCode.Denmark) {
        return [
          // content is set on the translation
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            key="privacy"
            className={styles.term}
            href="https://lendme.dk/kundeservice/vilkaar"
            target="_blank"
            rel="noreferrer"
          />,
          // content is set on the translation
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            key="privacy_policy"
            className={styles.term}
            href="https://lendme.dk/kundeservice/privatlivspolitik"
            target="_blank"
            rel="noreferrer"
          />,
        ];
      }

      if (marketCountry === MarketCountryCode.Norway) {
        return [
          <TermsUI
            key="general_terms"
            className={styles.term}
            term="general_terms"
            terms={terms}
          />,
          <TermsUI
            key="general_terms"
            className={styles.term}
            term="general_terms"
            terms={terms}
          />,
        ];
      }

      return [
        <TermsUI
          key="general_terms"
          className={styles.term}
          term="general_terms"
          terms={terms}
        />,
        <TermsUI
          key="data_retention"
          className={styles.term}
          term="data_retention"
          terms={terms}
        />,
      ];
    }

    return [
      <button
        key="terms-and-conditions"
        className={styles.term}
        onClick={(e) => {
          e.preventDefault();
          setSelectedTerm('GENERAL_TERMS');
        }}
        tabIndex={1}
        data-testid={'general-terms-button'}
      />,
      <button
        key="privacy-policy"
        className={styles.term}
        onClick={(e) => {
          e.preventDefault();
          setSelectedTerm('DATA_RETENTION');
        }}
        tabIndex={1}
        data-testid="data-retention-button"
      />,
    ];
  }, [external, marketCountry, terms]);

  return (
    <>
      <input
        type="hidden"
        {...register('LoanApplication/AcceptTermsConditions', {
          value: true,
          shouldUnregister: false,
        })}
      />
      {version && (
        <input
          type="hidden"
          {...register('LoanApplication/TermsAndConditionsText', {
            value: version,
            shouldUnregister: false,
          })}
        />
      )}

      {visible && (
        <>
          <Text>
            <Trans
              i18nKey="Consent.terms.label"
              t={t}
              components={termsComponents}
            />
          </Text>

          {selectedTerm && (
            <TermsModal
              term={selectedTerm}
              terms={terms}
              onClick={() => setSelectedTerm(undefined)}
            />
          )}
        </>
      )}
    </>
  );
};
